import { useState } from "react";
import { NavDropdown, InputGroup, Form } from "react-bootstrap";
import { COMPARES, GROUPS } from "dates/constants";
import { useDates } from "dates/DatesContext";
import moment from "moment";
import TrellisLink from "components/core/basic/TrellisLink";
import ThemeLine from "components/custom/merchandise/ThemeLine";
import useDashboardTheme, { SIDEBAR_COLORS, type SIDEBAR_SECTION } from "hooks/useDashboardTheme";
import placementIcon from "assets/images/icons/placement-w.png";
import pricingIcon from "assets/images/icons/pricing-w.png";
import productIcon from "assets/images/icons/product-w.png";
import promoIcon from "assets/images/icons/promotion-w.png";
import reportsIcon from "assets/images/icons/marketplace_intelligence.svg";
import { DialogTrigger, Button, Popover, Dialog, Label } from "react-aria-components";
import { Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { css, cx } from "../../../../styled-system/css";
import { ButtonGroup } from "../basic/ButtonGroup";
import { Datepicker } from "../../fields/base/Datepicker";
import { popoverStyles } from "../../fields/styles/styles";
import { cut } from "../../../utils/formatText";
import { useAppSelector } from "../../../redux/hooks";

const Icon = ({ section }: { section: SIDEBAR_SECTION }) => {
  let color = "#523673";
  let iconURL = placementIcon;

  if (section === "placement") {
    color = SIDEBAR_COLORS[section];
    iconURL = placementIcon;
  }
  if (section === "pricing") {
    color = SIDEBAR_COLORS[section];
    iconURL = pricingIcon;
  }

  if (section === "promotions") {
    color = SIDEBAR_COLORS[section];
    iconURL = promoIcon;
  }

  if (section === "product") {
    color = SIDEBAR_COLORS[section];
    iconURL = productIcon;
  }

  if (section === "reports") {
    iconURL = reportsIcon;
  }

  return (
    <div
      style={{
        width: "30px",
        height: "30px",
        backgroundColor: color,
        borderRadius: "100%",
      }}
      className="d-flex align-items-center justify-content-center me-3"
    >
      {iconURL && (
        <img
          src={iconURL}
          style={{
            width: "25px",
            height: "25px",
            borderRadius: "100%",
            // Changes svg color
            color: "#FFF",
          }}
          alt={`Trellis ${section} icon`}
        />
      )}
    </div>
  );
};

export interface Breadcrumb {
  searchable?: boolean;
  dropdownLinkOptions?: {
    name: string;
    href?: string;
    subTitle?: string;
    altDisplayMarkup?: React.ReactNode;
  }[];
  name: string;
  href?: string;
  searchPlaceholderText?: string;
  dropdownSubtitle?: string;
  unsorted?: boolean;
  activeOption?: string;
  default?: boolean;
  defaultHref?: string;
  defaultName?: string;
  defaultsubTitle?: string;
}

interface Props {
  items: Breadcrumb[];
  hideCompare?: boolean;
  hideRange?: boolean;
  children?: React.ReactNode;
  groupByOnly?: boolean;
  hideDateSelector?: boolean;
  hideGroupBy?: boolean;
}

const BreadcrumbDates = ({
  items,
  hideCompare = false,
  hideRange = false,
  children,
  groupByOnly,
  hideDateSelector,
  hideGroupBy = false,
}: Props) => {
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const [search, setSearch] = useState("");

  const { section } = useDashboardTheme();

  const MAX_BREADCRUMB_DROPDOWN_ITEMS = 200;

  return (
    <Flex
      className={css({
        ".container-fluid &": { marginX: "-7.5px" },
      })}
      justifyContent={"space-between"}
      borderBottom={"1px solid #dcdadd"}
      paddingX={"xlarge"}
      backgroundColor={"nav"}
    >
      <ol
        role="navigation"
        aria-label="breadcrumbs"
        className="breadcrumb d-flex align-items-center"
        style={{
          marginBottom: "0px",
          fontSize: "14px",
          margin: "revert",
        }}
      >
        <Icon section={section} />
        {channel !== "shopify" &&
          items.map((item, i) => {
            if (item.default && search.length === 0) {
              if (item.dropdownLinkOptions && item.dropdownLinkOptions[0]?.name !== item.defaultName) {
                item.dropdownLinkOptions.unshift({
                  name: item.defaultName!,
                  href: item.defaultHref!,
                  subTitle: item.defaultsubTitle ? item.defaultsubTitle : "",
                });
              }
            }
            return (
              <li key={i} className="breadcrumb-item ps-2 fw-normal">
                {item.dropdownLinkOptions ?
                  <NavDropdown
                    key={i}
                    id="DateBreadcrumbComparison"
                    renderMenuOnMount={true}
                    // TODO: Maybe below is better left out so search is persistent - get feedback of preference
                    onMouseLeave={() => setSearch("")}
                    className="fs-standard noarrow dropdown mx-0"
                    title={
                      <div style={{ whiteSpace: "normal", lineHeight: "12px" }}>
                        <TrellisLink className="text-decoration-none " to={item.href ?? "#"}>
                          {cut(item.default && !item.name ? item.defaultName : item.name, 100)}
                        </TrellisLink>
                      </div>
                    }
                  >
                    {item.searchable && (
                      <div className="px-3 py-2">
                        <InputGroup className="mb-3">
                          <InputGroup.Text
                            style={{
                              borderTopLeftRadius: "45px",
                              borderBottomLeftRadius: "45px",
                            }}
                          >
                            <i className="fa fa-search" />
                          </InputGroup.Text>
                          <Form.Control
                            placeholder={item.searchPlaceholderText ? item.searchPlaceholderText : "Search..."}
                            aria-label="Search Term"
                            onChange={(e) => {
                              setSearch(e.target.value);
                            }}
                            value={search}
                          />
                        </InputGroup>
                      </div>
                    )}
                    {item.dropdownSubtitle && (
                      <h3
                        className="m-0 p-2 px-3 fs-3"
                        style={{
                          color: "#2e0054",
                          fontWeight: 600,
                        }}
                      >
                        {item.dropdownSubtitle}
                      </h3>
                    )}
                    <div
                      style={{
                        width: "300px",
                        maxHeight: "500px",
                        overflowY: "auto",
                      }}
                    >
                      {item.dropdownLinkOptions
                        .filter((nestedLink) => {
                          if (search) {
                            return `${nestedLink.name} ${nestedLink.href}`.toLowerCase().includes(search.toLowerCase());
                          }

                          return true;
                        })
                        .sort((a, b) => {
                          if (item.unsorted) {
                            return 0;
                          }

                          // Always keep "All Categories" at the top
                          if (a.name === "All Categories") return -1;
                          if (b.name === "All Categories") return 1;

                          return (
                            a.name.toLowerCase() < b.name.toLowerCase() ? -1
                            : a.name.toLowerCase() > b.name.toLowerCase() ? 1
                            : 0
                          );
                        })
                        .splice(0, MAX_BREADCRUMB_DROPDOWN_ITEMS)
                        .map((nestedLink) => (
                          <div className="d-flex flex-column py-2" key={`${nestedLink.name}-${nestedLink.href ?? "#"}`}>
                            <TrellisLink
                              className={`text-decoration-none px-3`}
                              to={nestedLink.href ?? "#"}
                              style={{
                                fontWeight:
                                  (
                                    window.location.href.includes(nestedLink.href!) ||
                                    nestedLink.name === item.activeOption
                                  ) ?
                                    600
                                  : "inherit",
                              }}
                            >
                              {nestedLink.altDisplayMarkup ? nestedLink.altDisplayMarkup : nestedLink.name}
                              {nestedLink.subTitle ?
                                <ThemeLine additionalStyles={{ fontSize: "10px" }}>{nestedLink.subTitle}</ThemeLine>
                              : ""}
                            </TrellisLink>
                          </div>
                        ))}
                    </div>
                  </NavDropdown>
                : <TrellisLink className="text-decoration-none " to={item.href!}>
                    {item.name}
                  </TrellisLink>
                }
              </li>
            );
          })}
      </ol>
      <HStack>
        {!hideDateSelector && (
          <DateSelector
            hideCompare={hideCompare}
            hideGroupBy={hideGroupBy}
            hideRange={hideRange}
            groupByOnly={groupByOnly}
          />
        )}
        {children}
      </HStack>
    </Flex>
  );
};

export default BreadcrumbDates;

interface DateSelectorProps {
  hideCompare?: boolean;
  hideRange?: boolean;
  groupByOnly?: boolean;
  hideGroupBy?: boolean;
}

function DateSelector({
  hideCompare = false,
  hideGroupBy = false,
  groupByOnly = false,
  hideRange = false,
}: DateSelectorProps) {
  const {
    period,
    compare,
    group,
    setGroup,
    setPeriod,
    setCompare,
    end,
    start,
    preStart,
    preEnd,
    setStart,
    setEnd,
    setPreStart,
    setPreEnd,
  } = useDates();
  const selectedGroup = GROUPS.find((g) => g.id === group)!;

  return (
    <DialogTrigger>
      <Button
        className={css({
          all: "unset",
          cursor: "pointer",
          display: "flex",
          gap: "small",
          color: "royalPurple",
          lineHeight: "12px",
          _after: {
            alignSelf: "center",
            borderLeft: "4px solid transparent",
            borderRight: "4px solid transparent",
            borderTop: "4px solid",
            borderBottom: 0,
            width: 0,
            height: 0,
            content: '""',
          },

          _focusVisible: {
            outline: "1px auto {colors.bootstrapBlue}",
            outlineOffset: "1px",
          },
        })}
      >
        <VStack gap="0" justifyContent={"center"} alignItems={"start"}>
          <strong className={css({ fontSize: "medium" })}>
            {start.format("MMM D 'YY")} - {end.format("MMM D 'YY")}
          </strong>
          <small>
            by {GROUPS.find((g) => group === g.id)?.name}{" "}
            {hideCompare ? null : `vs. ${COMPARES.find((c) => compare === c.id)?.name}`}
          </small>
        </VStack>
      </Button>
      <Popover>
        <Dialog
          className={cx(
            popoverStyles,
            css({
              gap: "small",
              width: "380px",
              padding: "medium",
            }),
          )}
        >
          {!hideGroupBy && (
            <HStack justifyContent={"space-between"}>
              <Label className={css({ fontSize: "medium" })}>Group by:</Label>
              <ButtonGroup
                buttons={GROUPS.map((g) => ({ label: g.name, value: g.id }))}
                value={group}
                onSelect={setGroup}
                colorPalette={"buttonGroup.fuchsia"}
              />
            </HStack>
          )}
          {!groupByOnly && (
            <>
              {!hideRange && (
                <>
                  <Label className={css({ fontSize: "medium" })}>Range:</Label>
                  <ButtonGroup
                    compact
                    buttons={selectedGroup.periods.map((p) => ({ label: p.name, value: p.id }))}
                    value={period}
                    onSelect={setPeriod}
                    className={css({ alignSelf: "center" })}
                    colorPalette={"buttonGroup.fuchsia"}
                  />
                  <HStack justifyContent={"space-between"}>
                    <Datepicker
                      disabled={period !== "custom"}
                      label={"Start"}
                      value={start.toISOString()}
                      onChange={(d) => setStart(moment(d ?? undefined))}
                      maxDate={moment().toDate()}
                    />
                    <Datepicker
                      disabled={period !== "custom"}
                      label={"End"}
                      value={end.toISOString()}
                      onChange={(d) => setEnd(moment(d ?? undefined))}
                      maxDate={moment().toDate()}
                    />
                  </HStack>
                </>
              )}
              {!hideCompare && (
                <>
                  <HStack justifyContent={"space-between"}>
                    <Label className={css({ fontSize: "medium" })}>Compare against:</Label>
                    <ButtonGroup
                      buttons={COMPARES.map((c) => ({ label: c.name, value: c.id }))}
                      value={compare}
                      onSelect={setCompare}
                      colorPalette={"buttonGroup.fuchsia"}
                    />
                  </HStack>
                  <HStack justifyContent={"space-between"}>
                    <Datepicker
                      disabled={compare !== "custom"}
                      label={"Start"}
                      value={preStart.toISOString()}
                      onChange={(d) => setPreStart(moment(d ?? undefined))}
                      maxDate={moment().toDate()}
                    />
                    <Datepicker
                      disabled={compare !== "custom"}
                      label={"End"}
                      value={preEnd.toISOString()}
                      onChange={(d) => setPreEnd(moment(d ?? undefined))}
                      maxDate={moment().toDate()}
                    />
                  </HStack>
                </>
              )}
            </>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
